import { useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import { CurrencySymbol, UserType, WorkOrderStatusType } from "../../../utils/enum";
import { displayFormattedTotalTimelog, formatTwoDecimals } from "../../../utils/helpers";
import Expense from "../components/Expense";
import EditCustomerRatesModal from "../components/EditCustomerRatesModal";
import { useSearchParams } from "react-router-dom";
const CustomerRates = ({
  payment,
  paymentType,
  status,
  customerStatus,
  authRole,
  userAllowedActions,
  mode = "view",
  hasUpdateAccess,
  poOnly = false,
}) => {
  const [searchParams] = useSearchParams();
  const fromCustomerBoard = Boolean(searchParams.get("fromCustomerBoard"));
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Pre-approval the work order's own status drives everything: customerStatus is
  // still null, so it can't be judged on. A customer-raised ticket sits in
  // PENDING_APPROVAL until a client publishes it — the window where its rates and
  // purchase orders are still being filled in.
  const isEditableBeforeApproval = [
    // A draft is the earliest point in that window — it is still being composed,
    // so its rates are exactly what the author is there to fill in.
    WorkOrderStatusType.DRAFT,
    WorkOrderStatusType.PENDING_APPROVAL,
    WorkOrderStatusType.PUBLISHED,
    WorkOrderStatusType.ASSIGNED,
    WorkOrderStatusType.INPROGRESS,
    WorkOrderStatusType.WORKDONE,
    WorkOrderStatusType.VERIFIED,
  ].includes(status);

  const canEditRates =
    poOnly ||
    // Case 1: Internal flow
    (!fromCustomerBoard && isEditableBeforeApproval) ||
    // Case 2: Customer board flow — approval onwards is tracked on customerStatus,
    // and anything earlier falls back to the status above.
    (fromCustomerBoard &&
      ([
        WorkOrderStatusType.CANCELLED_PAYABLE,
        WorkOrderStatusType.APPROVED,
        WorkOrderStatusType.INVOICED,
      ].includes(customerStatus) ||
        (!customerStatus && isEditableBeforeApproval)));

  return (
    <BaseCard
      className={`pt-4 pb-6 ${mode === "view" && "xl:px-56 md:px-16"}  px-5 rounded-lg !mb-[0px]`}
      elevation={mode === "view" ? 1 : 0}
    >
      <div className="border-bottom mb-4 flex items-center justify-between pb-1">
        <div className="flex items-center pb-1">
          {/* "Customer Rates" is how a client refers to what they charge their
              customer. Viewed by that customer it is simply their payment. */}
          <h6 className={"!text-secondary"}>
            {authRole === UserType.CUSTOMER ? "Payment" : "Customer Rates"}
          </h6>
          <span className="text-xs ml-3 text-[#686868]">
            ({paymentType === "HOURLY" && "Hourly"}
            {paymentType === "HALF_DAY" && "Half Day"}
            {paymentType === "FULL_DAY" && "Full Day"}
            {paymentType === "FIXED" && "Fixed"}
            {/* A customer is billed on the same terms the client set, so they see the
                same label for them. */}
            {[UserType.CLIENT, UserType.CUSTOMER].includes(authRole) &&
              paymentType === "FTH" &&
              "First two hours"}
            {[UserType.CLIENT, UserType.CUSTOMER].includes(authRole) &&
              paymentType === "CUSTOM_HOURS" &&
              "Fixed Hours"}
            {authRole === UserType.FREELANCER && paymentType === "FTH" && "Hourly"})
            {canEditRates && hasUpdateAccess && (
              <i
                className="fa-light fa-pencil text-gray-900 text-base ml-3 cursor-pointer"
                onClick={() => setIsModalOpen(true)}
              ></i>
            )}
          </span>
        </div>
        <h6 className="!text-secondary font-medium">
          {CurrencySymbol[payment?.currency] || ""} {formatTwoDecimals(payment?.totalLabourCost)}
        </h6>
      </div>

      <BaseCard className="mt-4" elevation={0}>
        {payment?.purchaseOrders?.length > 0 ? (
          payment.purchaseOrders.map((po) => (
            <div key={po.id} className="flex items-center mb-2">
              <p className="mr-2 text-gray-700">Purchase Order #</p>
              <div className="border-t border-dotted border-gray-500 flex-grow"></div>
              <p className="ml-5 text-gray-700 text-right flex items-center">
                {po?.attachment?.url && (
                  <i
                    className="fa-solid fa-paperclip text-primary text-xs mr-1 cursor-pointer"
                    onClick={() => window.open(po.attachment.url, "_blank", "noopener,noreferrer")}
                  ></i>
                )}
                <span>{po.number || "-"}</span>
              </p>
            </div>
          ))
        ) : (
          <>
            {payment?.purchaseOrderNumber && (
              <div className="flex items-center mb-2">
                <p className="mr-2 text-gray-700">Purchase Order #</p>
                <div className="border-t border-dotted border-gray-500 flex-grow"></div>

                {payment?.purchaseOrderNumber ? (
                  <p className="ml-5 text-gray-700 text-right">{payment?.purchaseOrderNumber}</p>
                ) : (
                  <p className="ml-5 text-gray-700 text-right">-</p>
                )}
              </div>
            )}
          </>
        )}

        {paymentType === "HOURLY" && (
          <>
            <div className="flex items-center mb-2">
              <p className="mr-2">Hourly rate</p>
              <div className="border-t border-dotted border-gray-500  flex-grow"></div>
              <p className="min-w-14 ml-5 font-normal">
                {CurrencySymbol[payment?.currency] || ""}{" "}
                {formatTwoDecimals(payment?.hourlyRate) || 0}
              </p>
            </div>
            <div className="flex items-center mb-2">
              <p className="mr-2">Minimum Payable hours</p>
              <div className="border-t border-dotted border-gray-500  flex-grow"></div>
              <p className="min-w-14 ml-5 ">
                {displayFormattedTotalTimelog(payment?.minimumHours) || 0}
              </p>
            </div>

            {payment?.additionalHourlyRate > 0 && (
              <div className="flex items-center mb-2">
                <p className="mr-2">Additional Hourly rate</p>
                <div className="border-t border-dotted border-gray-500  flex-grow"></div>
                <p className="min-w-14 ml-5 font-normal">
                  {CurrencySymbol[payment?.currency] || ""}{" "}
                  {formatTwoDecimals(payment?.additionalHourlyRate) || 0}
                </p>
              </div>
            )}
            {/* {payment?.hoursWorked > payment?.minimumHours && (
              <div className="flex items-center mb-2">
                <p className="mr-2">Payable Hours</p>
                <div className="border-t border-dotted border-gray-500  flex-grow"></div>
                <p className="min-w-14 ml-5 ">
                  {displayFormattedTotalTimelog(
                    payment?.hoursWorked > payment?.minimumHours
                      ? payment?.hoursWorked
                      : payment?.minimumHours
                  ) || 0}{" "}
                </p>
              </div>
            )} */}
          </>
        )}
        {paymentType === "FIXED" && (
          <div className="flex items-center mb-2">
            <p className="mr-2">Fixed rate</p>
            <div className="border-t border-dotted border-gray-500  flex-grow"></div>
            <p className="min-w-14 ml-5 font-normal">
              {CurrencySymbol[payment?.currency] || ""}{" "}
              {formatTwoDecimals(payment?.fixedAmount) || 0}
            </p>
          </div>
        )}
        {paymentType === "HALF_DAY" && (
          <div className="flex items-center mb-2">
            <p className="mr-2">Half Day rate</p>
            <div className="border-t border-dotted border-gray-500  flex-grow"></div>
            <p className="min-w-14 ml-5 font-normal">
              {CurrencySymbol[payment?.currency] || ""}{" "}
              {formatTwoDecimals(payment?.halfDayRate) || 0}
            </p>
          </div>
        )}
        {paymentType === "FTH" && (
          <div className="flex items-center mb-2">
            <p className="mr-2">First two hours rate</p>
            <div className="border-t border-dotted border-gray-500  flex-grow"></div>
            <p className="min-w-14 ml-5 font-normal">
              {CurrencySymbol[payment?.currency] || ""} {formatTwoDecimals(payment?.fthRate) || 0}
            </p>
          </div>
        )}
        {paymentType === "CUSTOM_HOURS" && (
          <div className="flex items-center mb-2">
            <p className="mr-2">{payment?.customHours} hours rate</p>
            <div className="border-t border-dotted border-gray-500  flex-grow"></div>
            <p className="min-w-14 ml-5 font-normal">
              {CurrencySymbol[payment?.currency] || ""}{" "}
              {formatTwoDecimals(payment?.customHoursRate) || 0}
            </p>
          </div>
        )}
        {paymentType === "FULL_DAY" && (
          <div className="flex items-center mb-2">
            <p className="mr-2">Full Day rate</p>
            <div className="border-t border-dotted border-gray-500  flex-grow"></div>
            <p className="min-w-14 ml-5 font-normal">
              {CurrencySymbol[payment?.currency] || ""}{" "}
              {formatTwoDecimals(payment?.fullDayRate) || 0}
            </p>
          </div>
        )}
        {(paymentType === "HALF_DAY" ||
          paymentType === "FULL_DAY" ||
          paymentType === "FTH" ||
          paymentType === "CUSTOM_HOURS") && (
          <div className="flex items-center mb-2">
            <p className="mr-2">Additional Hourly rate</p>
            <div className="border-t border-dotted border-gray-500  flex-grow"></div>
            <p className="min-w-14 ml-5 font-normal">
              {CurrencySymbol[payment?.currency] || ""}{" "}
              {formatTwoDecimals(payment?.hourlyRate) || 0}
            </p>
          </div>
        )}

        {(paymentType === "FTH" ||
          paymentType === "CUSTOM_HOURS" ||
          paymentType === "HALF_DAY" ||
          paymentType === "FULL_DAY" ||
          paymentType === "HOURLY") &&
          payment?.additionalHours > 0 && (
            <div className="flex items-center mb-2">
              <p className="mr-2">Additional Payable hours</p>
              <div className="border-t border-dotted border-gray-500  flex-grow"></div>
              <p className="min-w-14 ml-5 font-normal">
                {displayFormattedTotalTimelog(payment?.additionalHours) || 0}
              </p>
            </div>
          )}

        {(paymentType === "HOURLY" ||
          paymentType === "HALF_DAY" ||
          paymentType === "FULL_DAY" ||
          paymentType === "FTH" ||
          paymentType === "CUSTOM_HOURS") &&
          payment?.nonBusinessHourRate > 0 && (
            <div className="flex items-center mb-2">
              <p className="mr-2">OOH Rates</p>
              <div className="border-t border-dotted border-gray-500  flex-grow"></div>
              <p className="min-w-14 ml-5 font-normal">
                {CurrencySymbol[payment?.currency] || ""}{" "}
                {formatTwoDecimals(payment.nonBusinessHourRate) || 0}
              </p>
            </div>
          )}
        {(paymentType === "HOURLY" ||
          paymentType === "HALF_DAY" ||
          paymentType === "FULL_DAY" ||
          paymentType === "FTH" ||
          paymentType === "CUSTOM_HOURS") &&
          payment?.nonBusinessHourRate > 0 && (
            <div className="flex items-center mb-2">
              <p className="mr-2">OOH Payable Hours</p>
              <div className="border-t border-dotted border-gray-500  flex-grow"></div>
              <p className="min-w-14 ml-5 font-normal">
                {displayFormattedTotalTimelog(payment.nonBusinessHours)}
              </p>
            </div>
          )}

        {/* {paymentType === "HOURLY" && status === "WORKDONE" && (
          <div className="flex items-center mb-2 ">
            <p className="mr-2 text-primary font-medium	">
              Actual Working Hours
            </p>
            <div className="border-t border-dotted border-primary  flex-grow"></div>
            <p className="min-w-14 ml-5 text-primary font-medium">
              {status === "WORKDONE" ||
              status === "APPROVED" ||
              status === "INCOMPLETE"
                ? ` {CurrencySymbol[payment?.currency] || "" }{" "}{payment?.hoursWorked} hrs`
                : "Pending"}
            </p>
          </div>
        )} */}

        {payment?.expenses?.length > 0 && (
          <section className="mt-6">
            <div className="border-bottom mb-4 flex justify-between pb-3 mt-5">
              <h6 className="!text-primary">Expenses</h6>
              <h6 className="text-primary font-medium">
                {CurrencySymbol[payment?.currency] || ""}{" "}
                {formatTwoDecimals(payment?.totalExpenseCost)}
              </h6>
            </div>
            {payment?.expenses.map((expense) => (
              <Expense
                key={expense?.id}
                expenseType={expense?.expenseType}
                description={expense?.description}
                amount={expense?.amount}
                addedBy={expense?.addedBy?.role.role}
                isFromCounterOffer={!!expense?.offerId}
                isApproved={expense?.isApproved}
                currency={payment?.currency}
                attachment={expense?.attachment}
              />
            ))}
          </section>
        )}
      </BaseCard>
      <div
        className={`flex items-center mt-6 justify-between ${
          status === "CANCELLED" ? "bg-[#F44336]" : "bg-secondary"
        } px-3 py-2 rounded-md`}
      >
        <h6 className="font-normal !text-white">
          {status === "WORKDONE"
            ? mode === "view"
              ? "Estimated Payable"
              : "Total Payable"
            : status === "APPROVED"
              ? "Total Payable"
              : status === "PAID"
                ? "Total Paid"
                : status === "CANCELLED"
                  ? "Grand Total"
                  : "Minimum Payable"}
        </h6>

        <h6 className="font-normal !text-white">
          {`${payment?.currency} `}
          {status === "WORKDONE" || status === "APPROVED" || status === "CANCELLED"
            ? formatTwoDecimals(payment?.grandTotal)
            : formatTwoDecimals(payment?.totalWorkOrderCost)}
        </h6>
      </div>

      {mode === "view" && (
        <div className="mt-2 justify-between  py-1 rounded-md">
          <p className={status === "CANCELLED" ? "text-[#F44336]" : "text-secondary"}>
            {status === "CANCELLED"
              ? userAllowedActions?.isWorkOrderCancelByClient
                ? `The above calculation is estimated cost of the work order. The actual cost will be calculated based on actual working time to complete this job.`
                : "No payment will be processed as this work order has been cancelled by Service Provider."
              : status === "WORKDONE"
                ? "This calculation reflects the estimated payable amount based on your time log, pending client confirmation."
                : status === "APPROVED"
                  ? "The final payment has been calculated based on the completed work, verified time log and approved by client."
                  : status === "PAID"
                    ? "Payment completed. The final amount has been successfully paid."
                    : "This is the minimum payable amount; however, the final payment will be determined upon job completion based on the actual time worked."}
          </p>
        </div>
      )}
      {isModalOpen && (
        <EditCustomerRatesModal
          open={isModalOpen}
          close={() => setIsModalOpen(false)}
          paymentData={{
            ...payment,
            paymentType,
            // purchaseOrderFile
          }}
          poOnly={poOnly}
        />
      )}
      {/* <ServiceFee payment={payment} status={status} /> */}
    </BaseCard>
  );
};

export default CustomerRates;
